import {
    Component,
    input,
    signal,
    effect,
    viewChild,
    forwardRef,
    Type,
    Signal,
    InputSignal,
    WritableSignal,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

import { Extension } from "@codemirror/state";
import { CodeEditor } from "@acrodata/code-editor";
import { LanguageDescription } from "@codemirror/language";

import { OnChangeFn, OnTouchedFn } from "../../models";
import { HtmlEditorThemeService } from "../services/html-editor-theme.service";
import { HtmlEditorLanguageService } from "../services/html-editor-language.service";


@Component({
    selector: "html-editor",
    templateUrl: "./html-editor.component.html",
    styleUrls: ["./html-editor.component.scss"],
    providers: [{
        useExisting: forwardRef((): Type<HtmlEditorComponent> => HtmlEditorComponent),
        provide: NG_VALUE_ACCESSOR,
        multi: true
    }]
})
export class HtmlEditorComponent implements ControlValueAccessor {
    // View child statements.
    public editor: Signal<CodeEditor> = viewChild(CodeEditor);

    // Input properties.
    public extensions: InputSignal<Extension[]> = input([]);

    // Writable signals.
    private onTouched: WritableSignal<OnTouchedFn> = signal(null);
    private onChange: WritableSignal<OnChangeFn> = signal(null);

    protected language: WritableSignal<LanguageDescription> = signal(null);
    protected disabled: WritableSignal<boolean> = signal(false);
    protected theme: WritableSignal<Extension> = signal(null);
    protected value: WritableSignal<string> = signal("");


    constructor(private languageService: HtmlEditorLanguageService,
                private themeService: HtmlEditorThemeService) {
        this.configureEditor();
    }

    private configureEditor(): void {
        // Set the editor theme (one-dark theme with minor overrides).
        this.theme.set(this.themeService.getThemeExtension());

        // Set the editor language.
        this.language.set(this.languageService.getLanguageDescription());

        // React to editor apparition or on-touched value change, to update the latter in the editor.
        effect((): void => {
            const editor: CodeEditor = this.editor();
            if (!editor) return;

            const onTouched: OnTouchedFn = this.onTouched();
            editor.registerOnTouched((): void => onTouched?.());
        });

        // React to editor apparition or on-change value change, to update the latter in the editor.
        effect((): void => {
            const editor: CodeEditor = this.editor();
            if (!editor) return;

            const onChange: OnChangeFn = this.onChange();
            editor.registerOnChange((value: string): void => onChange?.(value));
        });
    }

    public registerOnChange(fn: OnChangeFn): void {
        this.onChange.set(fn);
    }

    public registerOnTouched(fn: OnTouchedFn): void {
        this.onTouched.set(fn);
    }

    public writeValue(value: string): void {
        this.value.set(value);
    }

    public setDisabledState(disabled: boolean): void {
        this.disabled.set(disabled);
    }
}